import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { PasswordInput } from '../components/ui/password-input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Upload, FileKey, Check, X, AlertTriangle, Clock, Send, 
  RefreshCw, Trash2, Settings, FileText, User, Calendar, Eye, Loader2,
  Download, FileCode, Archive, ExternalLink, Code
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch with httpOnly cookies
const authFetch = async (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
    }
  });
};

export default function ESocialConfig() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [certStatus, setCertStatus] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [sendPassword, setSendPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [employees, setEmployees] = useState([]);
  
  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPassword, setUploadPassword] = useState('');
  const fileInputRef = useRef(null);
  
  // New event form
  const [newEvent, setNewEvent] = useState({
    employee_id: '',
    event_type: '',
    data: {}
  });

  useEffect(() => {
    if (!authLoading && user) {
      fetchData();
    }
  }, [authLoading, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, certRes, typesRes, eventsRes, empRes] = await Promise.all([
        authFetch(`${API}/api/esocial/status`),
        authFetch(`${API}/api/esocial/certificate/status`),
        authFetch(`${API}/api/esocial/event-types`),
        authFetch(`${API}/api/esocial/events`),
        authFetch(`${API}/api/employees`)
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (certRes.ok) setCertStatus(await certRes.json());
      if (typesRes.ok) setEventTypes(await typesRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (empRes.ok) setEmployees(await empRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/api/esocial/events/${eventToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Evento excluído com sucesso');
        setShowDeleteModal(false);
        setEventToDelete(null);
        fetchData(); // Refresh the list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir evento');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erro ao excluir evento');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pfx') && !file.name.toLowerCase().endsWith('.p12')) {
        toast.error('Selecione um arquivo .pfx ou .p12');
        return;
      }
      setUploadFile(file);
    }
  };

  const handleUploadCertificate = async () => {
    if (!uploadFile || !uploadPassword) {
      toast.error('Selecione o arquivo e informe a senha');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('password', uploadPassword);

      const response = await authFetch(`${API}/api/esocial/certificate/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadPassword('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao fazer upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao fazer upload do certificado');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCertificate = async () => {
    if (!window.confirm('Tem certeza que deseja remover o certificado?')) return;

    try {
      const response = await authFetch(`${API}/api/esocial/certificate`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Certificado removido');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao remover certificado');
      }
    } catch (error) {
      toast.error('Erro ao remover certificado');
    }
  };

  const handleChangeEnvironment = async (env) => {
    try {
      const response = await authFetch(`${API}/api/esocial/environment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ environment: env })
      });

      if (response.ok) {
        toast.success(`Ambiente alterado para ${env === 'producao' ? 'Produção' : 'Homologação'}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao alterar ambiente');
      }
    } catch (error) {
      toast.error('Erro ao alterar ambiente');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.employee_id || !newEvent.event_type) {
      toast.error('Selecione o funcionário e o tipo de evento');
      return;
    }

    try {
      const response = await authFetch(`${API}/api/esocial/events/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEvent)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowEventModal(false);
        setNewEvent({ employee_id: '', event_type: '', data: {} });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar evento');
      }
    } catch (error) {
      toast.error('Erro ao criar evento');
    }
  };

  const handleSendEvent = async () => {
    if (!sendPassword) {
      toast.error('Informe a senha do certificado');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('password', sendPassword);

      const response = await authFetch(`${API}/api/esocial/events/${selectedEvent.id}/send`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowSendModal(false);
        setSendPassword('');
        setSelectedEvent(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar evento');
      }
    } catch (error) {
      toast.error('Erro ao enviar evento');
    } finally {
      setSending(false);
    }
  };

  // ============= EXPORT FUNCTIONS =============

  const handleExportXML = async (eventId, signed = false) => {
    try {
      const endpoint = signed ? 'signed-xml' : 'xml';
      
      const response = await authFetch(`${API}/api/esocial/events/${eventId}/export/${endpoint}`);

      if (response.ok) {
        const blob = await response.blob();
        const filename = response.headers.get('content-disposition')?.split('filename=')[1] || 'evento.xml';
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success('XML exportado com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao exportar XML');
      }
    } catch (error) {
      toast.error('Erro ao exportar XML');
    }
  };

  const handleExportBatch = async () => {
    if (selectedEvents.length === 0) {
      toast.error('Selecione pelo menos um evento para exportar');
      return;
    }

    setExporting(true);
    try {
      const response = await authFetch(`${API}/api/esocial/events/export/batch`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedEvents)
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = response.headers.get('content-disposition')?.split('filename=')[1] || 'esocial_eventos.zip';
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success(`${selectedEvents.length} evento(s) exportado(s) em ZIP!`);
        setSelectedEvents([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao exportar eventos');
      }
    } catch (error) {
      toast.error('Erro ao exportar eventos');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = async (eventStatus = 'pending') => {
    setExporting(true);
    try {
      const response = await authFetch(`${API}/api/esocial/events/export/all?status=${eventStatus}`);

      if (response.ok) {
        const blob = await response.blob();
        const filename = response.headers.get('content-disposition')?.split('filename=')[1] || 'esocial_eventos.zip';
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success('Eventos exportados com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao exportar eventos');
      }
    } catch (error) {
      toast.error('Erro ao exportar eventos');
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewXML = async (eventId) => {
    try {
      const response = await authFetch(`${API}/api/esocial/events/${eventId}/preview`);

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setShowPreviewModal(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao carregar preview');
      }
    } catch (error) {
      toast.error('Erro ao carregar preview');
    }
  };

  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const selectAllEvents = () => {
    if (selectedEvents.length === events.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(events.map(e => e.id));
    }
  };

  const getStatusBadge = (eventStatus) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pendente' },
      sent: { color: 'bg-green-100 text-green-800', text: 'Enviado' },
      error: { color: 'bg-red-100 text-red-800', text: 'Erro' },
      sign_error: { color: 'bg-red-100 text-red-800', text: 'Erro Assinatura' },
      simulated: { color: 'bg-gray-100 text-gray-800', text: 'Simulado' }
    };
    const badge = badges[eventStatus] || badges.pending;
    return <Badge className={badge.color}>{badge.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="esocial-config">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Integração e-Social</h1>
          <p className="text-gray-600">Configure seu certificado digital para envio de eventos SST</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Certificate Status Card */}
      <Card className="border-l-4 border-l-[#0d9488]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileKey className="h-5 w-5 text-[#0d9488]" />
            Certificado Digital A1
          </CardTitle>
          <CardDescription>
            Configure seu certificado digital para assinar e enviar eventos ao e-Social
          </CardDescription>
        </CardHeader>
        <CardContent>
          {certStatus?.configured ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {certStatus.is_expired ? (
                  <Badge className="bg-red-100 text-red-800">
                    <X className="h-3 w-3 mr-1" />
                    Expirado
                  </Badge>
                ) : certStatus.days_until_expiry <= 30 ? (
                  <Badge className="bg-yellow-100 text-yellow-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Vence em {certStatus.days_until_expiry} dias
                  </Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="h-3 w-3 mr-1" />
                    Válido
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Titular:</span>
                  <p className="font-medium">{certStatus.subject}</p>
                </div>
                <div>
                  <span className="text-gray-500">Emissor:</span>
                  <p className="font-medium">{certStatus.issuer}</p>
                </div>
                <div>
                  <span className="text-gray-500">CNPJ:</span>
                  <p className="font-medium">{certStatus.cnpj || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Validade:</span>
                  <p className="font-medium">
                    {certStatus.valid_until ? new Date(certStatus.valid_until).toLocaleDateString('pt-BR') : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" size="sm" onClick={() => setShowUploadModal(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Substituir Certificado
                </Button>
                <Button variant="destructive" size="sm" onClick={handleRemoveCertificate}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remover
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Nenhum certificado digital configurado</p>
              <Button onClick={() => setShowUploadModal(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Fazer Upload do Certificado A1
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                Apenas certificados A1 (.pfx ou .p12) são suportados
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Environment Settings */}
      {certStatus?.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurações do Ambiente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label>Ambiente de Envio:</Label>
              <Select 
                value={status?.environment || 'producao_restrita'}
                onValueChange={handleChangeEnvironment}
              >
                <SelectTrigger className="w-[250px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="producao_restrita">
                    Produção Restrita (Homologação)
                  </SelectItem>
                  <SelectItem value="producao">
                    Produção
                  </SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="ml-2">
                {status?.environment === 'producao' ? 'Eventos reais' : 'Ambiente de testes'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Eventos e-Social
              </CardTitle>
              <CardDescription>
                Gerencie eventos de SST (S-2210, S-2220, S-2240) - Exporte XMLs para envio manual
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedEvents.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={handleExportBatch}
                  disabled={exporting}
                  className="border-[#0d9488] text-[#0d9488]"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  Exportar {selectedEvents.length} selecionado(s)
                </Button>
              )}
              <Select onValueChange={(value) => handleExportAll(value)}>
                <SelectTrigger className="w-[180px]">
                  <Download className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Exportar Todos..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="error">Com Erro</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={() => setShowEventModal(true)}
                disabled={!certStatus?.configured}
                className="bg-[#0d9488] hover:bg-[#0b7d73]"
              >
                <Send className="h-4 w-4 mr-2" />
                Novo Evento
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Nenhum evento criado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Select All Header */}
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={selectedEvents.length === events.length && events.length > 0}
                  onChange={selectAllEvents}
                  className="w-4 h-4 rounded border-gray-300 text-[#0d9488] focus:ring-[#0d9488]"
                />
                <span className="text-sm text-gray-600">
                  {selectedEvents.length > 0 
                    ? `${selectedEvents.length} de ${events.length} selecionados`
                    : 'Selecionar todos'
                  }
                </span>
              </div>
              
              {events.map((event) => (
                <div 
                  key={event.id} 
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                    selectedEvents.includes(event.id) ? 'border-[#0d9488] bg-[#e8f4fc]/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(event.id)}
                      onChange={() => toggleEventSelection(event.id)}
                      className="w-4 h-4 rounded border-gray-300 text-[#0d9488] focus:ring-[#0d9488]"
                    />
                    <div className="bg-[#1e3a5f] text-white px-3 py-1 rounded text-sm font-mono">
                      {event.event_type}
                    </div>
                    <div>
                      <p className="font-medium">{event.event_description || event.event_type}</p>
                      <p className="text-sm text-gray-500">
                        {event.employee_name} • {new Date(event.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(event.status)}
                    {event.protocol && (
                      <span className="text-xs text-gray-500 font-mono">{event.protocol}</span>
                    )}
                    
                    {/* Export Actions */}
                    <div className="flex items-center gap-1 border-l pl-2 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreviewXML(event.id)}
                        title="Visualizar XML"
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleExportXML(event.id, false)}
                        title="Baixar XML"
                        className="h-8 w-8 p-0 text-[#0d9488] hover:text-[#0b7d73]"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {/* Delete button - only for pending/error events */}
                      {(event.status === 'pending' || event.status === 'error') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEventToDelete(event);
                            setShowDeleteModal(true);
                          }}
                          title="Excluir evento"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {event.status === 'pending' && (
                      <Button 
                        size="sm" 
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowSendModal(true);
                        }}
                        disabled={!certStatus?.configured || certStatus?.is_expired}
                        className="bg-[#0d9488] hover:bg-[#0b7d73] ml-2"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Enviar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <FileCode className="h-4 w-4" />
              Sobre a Exportação de XMLs
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>XML:</strong> Arquivo no formato exigido pelo e-Social para envio de eventos</li>
              <li>• <strong>ZIP:</strong> Exportação de múltiplos eventos compactados para download</li>
              <li>• <strong>Envio Manual:</strong> Você pode exportar os XMLs e enviá-los diretamente pelo portal do e-Social</li>
              <li>• <strong>Certificado A1:</strong> Necessário para assinar os eventos antes do envio</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Upload Certificate Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload do Certificado Digital A1</DialogTitle>
            <DialogDescription>
              Selecione o arquivo do certificado (.pfx ou .p12) e informe a senha
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Arquivo do Certificado</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={uploadFile?.name || ''}
                  placeholder="Selecione o arquivo..."
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Selecionar
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-gray-500">Formatos aceitos: .pfx, .p12</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cert-password">Senha do Certificado</Label>
              <PasswordInput
                id="cert-password"
                value={uploadPassword}
                onChange={(e) => setUploadPassword(e.target.value)}
                placeholder="Digite a senha do certificado"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUploadCertificate} disabled={uploading || !uploadFile}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Fazer Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Evento e-Social</DialogTitle>
            <DialogDescription>
              Selecione o funcionário e o tipo de evento SST
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Funcionário</Label>
              <Select 
                value={newEvent.employee_id}
                onValueChange={(value) => setNewEvent({...newEvent, employee_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} - {emp.cpf || 'CPF não informado'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Evento</Label>
              <Select 
                value={newEvent.event_type}
                onValueChange={(value) => setNewEvent({...newEvent, event_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de evento" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((type) => (
                    <SelectItem key={type.code} value={type.code}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 px-1 rounded">{type.code}</span>
                        <span>{type.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newEvent.event_type && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-800">
                  {eventTypes.find(t => t.code === newEvent.event_type)?.description}
                </p>
              </div>
            )}

            {/* Event-specific fields would go here based on event_type */}
            {newEvent.event_type === 'S-2210' && (
              <div className="space-y-3 border-t pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Data do Acidente</Label>
                    <Input 
                      type="date" 
                      onChange={(e) => setNewEvent({
                        ...newEvent, 
                        data: {...newEvent.data, data_acidente: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <Label>Hora</Label>
                    <Input 
                      type="time" 
                      onChange={(e) => setNewEvent({
                        ...newEvent, 
                        data: {...newEvent.data, hora_acidente: e.target.value.replace(':', '')}
                      })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Observação</Label>
                  <Input 
                    placeholder="Descreva o acidente..."
                    onChange={(e) => setNewEvent({
                      ...newEvent, 
                      data: {...newEvent.data, observacao: e.target.value}
                    })}
                  />
                </div>
              </div>
            )}

            {newEvent.event_type === 'S-2220' && (
              <div className="space-y-3 border-t pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo de Exame</Label>
                    <Select 
                      onValueChange={(value) => setNewEvent({
                        ...newEvent, 
                        data: {...newEvent.data, tipo_exame: value}
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Admissional</SelectItem>
                        <SelectItem value="1">Periódico</SelectItem>
                        <SelectItem value="2">Retorno ao trabalho</SelectItem>
                        <SelectItem value="3">Mudança de risco</SelectItem>
                        <SelectItem value="4">Demissional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data do ASO</Label>
                    <Input 
                      type="date" 
                      onChange={(e) => setNewEvent({
                        ...newEvent, 
                        data: {...newEvent.data, data_aso: e.target.value}
                      })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Resultado</Label>
                  <Select 
                    onValueChange={(value) => setNewEvent({
                      ...newEvent, 
                      data: {...newEvent.data, resultado: value}
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Apto</SelectItem>
                      <SelectItem value="2">Inapto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateEvent}>
              Criar Evento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Event Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Evento ao e-Social</DialogTitle>
            <DialogDescription>
              Informe a senha do certificado para assinar e enviar o evento
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-mono text-sm">{selectedEvent.event_type}</p>
                <p className="font-medium">{selectedEvent.event_description}</p>
                <p className="text-sm text-gray-500">{selectedEvent.employee_name}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="send-password">Senha do Certificado</Label>
                <PasswordInput
                  id="send-password"
                  value={sendPassword}
                  onChange={(e) => setSendPassword(e.target.value)}
                  placeholder="Digite a senha do certificado"
                />
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg mt-4 text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                O evento será enviado ao ambiente de{' '}
                <strong>{status?.environment === 'producao' ? 'Produção' : 'Homologação'}</strong>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendEvent} disabled={sending || !sendPassword}>
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Assinar e Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* XML Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-[#0d9488]" />
              Visualização do XML - {previewData?.event_type}
            </DialogTitle>
            <DialogDescription>
              {previewData?.employee_name} • Status: {previewData?.status}
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="py-4 space-y-4">
              {/* Event Info */}
              <div className="flex flex-wrap gap-2">
                {previewData.has_signed_xml && (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="h-3 w-3 mr-1" />
                    XML Assinado
                  </Badge>
                )}
                {previewData.protocol && (
                  <Badge className="bg-blue-100 text-blue-800">
                    Protocolo: {previewData.protocol}
                  </Badge>
                )}
                {previewData.sent_at && (
                  <Badge variant="outline">
                    Enviado: {new Date(previewData.sent_at).toLocaleString('pt-BR')}
                  </Badge>
                )}
              </div>

              {/* XML Content */}
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
                  <span className="text-sm text-gray-300">XML do Evento</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleExportXML(previewData.id, previewData.has_signed_xml)}
                    className="text-gray-300 hover:text-white"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Baixar
                  </Button>
                </div>
                <pre className="p-4 text-sm text-green-400 overflow-auto max-h-[400px] font-mono">
                  {previewData.xml}
                </pre>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-blue-50 rounded-lg text-sm">
                <h4 className="font-medium text-blue-800 mb-2">Como enviar manualmente ao e-Social:</h4>
                <ol className="list-decimal list-inside text-blue-700 space-y-1">
                  <li>Baixe o arquivo XML clicando no botão "Baixar"</li>
                  <li>Acesse o portal do e-Social: <a href="https://login.esocial.gov.br/" target="_blank" rel="noopener noreferrer" className="underline">login.esocial.gov.br</a></li>
                  <li>Utilize seu certificado digital para autenticação</li>
                  <li>Navegue até "Transmissão" → "Enviar Arquivo"</li>
                  <li>Selecione o arquivo XML baixado e confirme o envio</li>
                </ol>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Fechar
            </Button>
            <Button 
              onClick={() => handleExportXML(previewData?.id, previewData?.has_signed_xml)}
              className="bg-[#0d9488] hover:bg-[#0b7d73]"
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar XML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Event Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent data-testid="delete-event-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmar Exclusão
            </DialogTitle>
            <DialogDescription>
              Deseja excluir este evento? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          {eventToDelete && (
            <div className="py-4 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Tipo:</span>
                  <span className="text-sm font-medium">{eventToDelete.event_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Funcionário:</span>
                  <span className="text-sm font-medium">{eventToDelete.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Criado em:</span>
                  <span className="text-sm">{new Date(eventToDelete.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  O XML gerado para este evento será permanentemente removido do sistema.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteModal(false);
                setEventToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteEvent}
              disabled={deleting}
              data-testid="confirm-delete-event"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Evento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
